import React, { useMemo, useState } from 'react'
import GTable from '../../../../../components/elements/GTable';
import { addInsurerSalesToUnpaidPayout, getInsurerSales, getTeamSales } from '../../../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import { getVehicleCoverTypeFromKey, showToast } from '../../../../../utils/miscFunctions';
import moment from 'moment';
import Loader from '../../../../../components/Loader';
import { Payout } from '../../../../../models/payout/Payout';
import Button from '../../../../../components/form-control/Button';
import usePolicy from '../../../../../hooks/policyHook';

type AddSaleToPayoutModalProps = {
    payout: Payout;
    teamId?: string;
    onClose: () => void;
    onProceed?: () => void;
}

const AddSaleToPayoutModal = ({ payout, teamId, onClose, onProceed }: AddSaleToPayoutModalProps) => {
    const [selectedSales, setSelectedSales] = useState<string[]>([])
    const [searchString, setSearchString] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const { PolicyTypesBadge } = usePolicy();

    const columns = useMemo(
        () => [
            {
                id: 'selection',
                Header: ({ getToggleAllRowsSelectedProps }: any) => (
                    <div className="">
                        <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
                    </div>
                ),
                Cell: ({ row }: any) => (
                    <div className="">
                        <input type="checkbox" {...row.getToggleRowSelectedProps()} />
                    </div>
                )
            },
            {
                Header: 'M',
                columns: [
                    {
                        Header: 'id',
                        accessor: 'id',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Customer Name',
                        accessor: 'customerName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Policy Type',
                        accessor: 'policyType',
                        Cell: (row: any) => {
                            return (
                                <span>
                                    <PolicyTypesBadge policyType={row.cell.value} />
                                </span>
                            );
                        }
                    },
                    {
                        Header: 'Amount',
                        accessor: 'grossPremium',
                        Cell: (row: any) => {
                            return <span>&#162; {row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                ]
            }
        ],
        []
    );

    const { data: payments } = useQuery({
        queryKey: ["fetchInsurerPayments", searchString],
        queryFn: () => fetchInsurerPayments(),
    })

    const fetchInsurerPayments = async () => {
        try {
            // const response: any = await getInsurerSales(0, 100, searchString, undefined, undefined, teamId, 'false')
            const response = await getTeamSales(0, 100, Number(teamId), false, undefined,)

            if (response) {
                return response?.content ?? response;
            }
            return [];
        } catch (error: any) {
            showToast.error(error?.description)
        } finally {
            setIsLoading(false);
        }

    };

    const handleAddSalesToPayout = async () => {
        setIsSaving(true);
        try {
            const response: any = await addInsurerSalesToUnpaidPayout(payout.id, selectedSales)
            if (response) {
                showToast.success(response?.message ?? "Sales added to payout successfully")
                onProceed?.();
                onClose();
            }
        } catch (error: any) {
            showToast.error(error?.description ?? "Failed to add sales to payout")
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <div className="p-6">
            {isLoading ? <Loader message="Loading sales..." brollyLoaderFixed /> : (
                <div>
                    <div className="max-h-[70vh] overflow-y-auto">
                        <GTable
                            showCheckboxes
                            selectionChanged={(selectedObjects: any[], selectedIndexes: number[]) => {
                                const salesIds = selectedObjects.map((sale) => sale.id);
                                setSelectedSales(salesIds);
                            }}
                            columns={columns}
                            data={payments || []}
                            classes="h-fit"
                            tableType={'sales'}
                            loading={isLoading}
                            searchPlaceholder="Search by customer name"
                            onSearch={(searchString: string) => setSearchString(searchString)}
                        />
                    </div>
                    {selectedSales.length > 0 && (
                        <div className="w-full flex gap-4 mt-6">
                            <Button
                                type="button"
                                label="Cancel"
                                bgColor='secondary'
                  fullWidth
                                disabled={isSaving || isLoading}
                                onClick={onClose}
                            />
                            <Button
                                type="button"
                                onClick={handleAddSalesToPayout}
                                label="Add to Payout"
                                bgColor='primary'
              fullWidth
                                disabled={isSaving || isLoading}
                                loading={isSaving || isLoading}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default AddSaleToPayoutModal